import Configuration from './Configuration';

// ------
// GLOBAL FUNCTIONS
// ------

/**
 * Sort an array of width objects
 * @param {Array} a
 * @param {Array} b
 * @returns
 */
function sortWidthObjectsArray(a, b) {
  return a.width - b.width;
}

/**
 *
 * @param {Array} a
 * @param {Array} b
 * @returns
 */
function equalsArray(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.
  // Please note that calling sort on an array will modify that array.
  // you might want to clone your array first.

  for (var i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

// ------
// GLOBAL VARIABLES
// ------

/**
 * Identifier of the lastest created preset
 */
let LAST_PRESET_ID = 0;

/**
 * Global constant for the width choosable by the client
 */
const AVAILABLE_BOX_SIZES = [
  {
    height: 194.0,
    widths: [
      {
        width: 34.0,
        price: 129.0,
      },
      {
        width: 64.0,
        price: 149.0,
      },
      {
        width: 94.0,
        price: 169.0,
      },
    ],
  },
  {
    height: 232.0,
    widths: [
      {
        width: 34.0,
        price: 149.0,
      },
      {
        width: 64.0,
        price: 169.0,
      },
      {
        width: 94.0,
        price: 199.0,
      },
    ],
  },
];

// Force the sort of the array (to be sure that the smallest width comes first)
for (let i = 0; i < AVAILABLE_BOX_SIZES.length; i++) {
  AVAILABLE_BOX_SIZES.at(i).widths.sort(sortWidthObjectsArray);
}

// console.log(AVAILABLE_BOX_SIZES);

const CHOSEN_HEIGHT = 0; // DEBUG: Chosen height of 194 cm
let GENERATED_FINAL_PRESETS = [];

/**
 *
 * @param {Number} totalAvailableWidth The width of the client's wall that is available
 * @returns {Object}
 */
export function calculatePresets(totalAvailableWidth) {
  // Force the entry to be a number
  totalAvailableWidth = parseInt(totalAvailableWidth);
  if (Number.isNaN(totalAvailableWidth)) {
    return;
  }

  // Reset the ID
  LAST_PRESET_ID = 0;
  // Clear the presets list before trying to generate a new presets list
  GENERATED_FINAL_PRESETS = [];

  // Create the base population of Configuration objects
  AVAILABLE_BOX_SIZES.at(CHOSEN_HEIGHT).widths.forEach((widthObj) => {
    // Check if the value can fit in the total defined length before creating a new configuration
    if (widthObj.width <= totalAvailableWidth) {
      // Generate a first configuration before trying to create the children
      const rootConfiguration = new Configuration(++LAST_PRESET_ID, totalAvailableWidth);
      rootConfiguration.addBox(widthObj);

      // Create the new generations
      createNewGeneration(rootConfiguration);
    }
  });

  cleanPresets(GENERATED_FINAL_PRESETS);
  // console.log(GENERATED_FINAL_PRESETS);

  const smallestNumberOfElementsPresets = pickSmallestNumberOfElementsPresets(GENERATED_FINAL_PRESETS);
  const largestNumberOfElementsPresets = pickLargestNumberOfElementsPresets(GENERATED_FINAL_PRESETS);
  const lowestCostPresets = pickLowestCostPresets(GENERATED_FINAL_PRESETS);
  const closestWidthPresets = pickClosestWidthPresets(GENERATED_FINAL_PRESETS);

  return {
    smallestNumberOfElementsPresets: smallestNumberOfElementsPresets,
    largestNumberOfElementsPresets: largestNumberOfElementsPresets,
    lowestCostPresets: lowestCostPresets,
    closestWidthPresets: closestWidthPresets,
  };
}

/**
 * Main calculation loop (recursive)
 * @param {Configuration} parentConfiguration
 */
function createNewGeneration(parentConfiguration) {
  // Stop condition
  if (parentConfiguration.isFinal(AVAILABLE_BOX_SIZES.at(CHOSEN_HEIGHT).widths)) {
    GENERATED_FINAL_PRESETS.push(parentConfiguration);
    // console.log("pushed one preset: " + JSON.stringify(parentConfiguration.currentConfiguration));
    return;
  }

  // Create new leafs for the tree
  const children = parentConfiguration.createChildren(LAST_PRESET_ID + 1, AVAILABLE_BOX_SIZES.at(CHOSEN_HEIGHT).widths);

  LAST_PRESET_ID += children.length;

  // console.log("Created " + children.length + " children");
  children.forEach((childConfiguration) => {
    // console.log("child: " + JSON.stringify(childConfiguration.content));
    createNewGeneration(childConfiguration);
  });
}

/**
 *
 * @param {Array} presets The array containing the final presets
 */
function cleanPresets(presets) {
  // Sort all the presets data first
  for (let i = 0; i < presets.length; i++) {
    presets.at(i).currentConfiguration.sort(sortWidthObjectsArray);
  }

  for (let i = 0; i < presets.length; i++) {
    for (let j = i + 1; j < presets.length; j++) {
      if (equalsArray(presets.at(i).currentConfiguration, presets.at(j).currentConfiguration)) {
        presets.splice(i, 1);
        i--;
        break;
      }
    }
  }
}

/**
 *
 * @param {Array} presets The array containing the final presets
 * @returns {Array}
 */
function pickSmallestNumberOfElementsPresets(presets) {
  if (presets.length == 0) {
    return [];
  }

  if (presets.length == 1) {
    return presets;
  }

  let bestPresets = [presets.at(0)];

  for (let i = 1; i < presets.length; i++) {
    if (presets.at(i).currentConfiguration.length < bestPresets.at(0).currentConfiguration.length) {
      bestPresets = [];
      bestPresets.push(presets.at(i));
    } else if (presets.at(i).currentConfiguration.length == bestPresets.at(0).currentConfiguration.length) {
      bestPresets.push(presets.at(i));
    }
  }

  return pickClosestWidthPresets(bestPresets);
}

/**
 *
 * @param {Array} presets The array containing the final presets
 * @returns {Array}
 */
function pickLargestNumberOfElementsPresets(presets) {
  if (presets.length == 0) {
    return [];
  }

  if (presets.length == 1) {
    return presets;
  }

  let bestPresets = [presets.at(0)];

  for (let i = 1; i < presets.length; i++) {
    if (presets.at(i).currentConfiguration.length > bestPresets.at(0).currentConfiguration.length) {
      bestPresets = [];
      bestPresets.push(presets.at(i));
    } else if (presets.at(i).currentConfiguration.length == bestPresets.at(0).currentConfiguration.length) {
      bestPresets.push(presets.at(i));
    }
  }

  return pickClosestWidthPresets(bestPresets);
}

/**
 *
 * @param {Array} presets The array containing the final presets
 * @returns {Array}
 */
function pickLowestCostPresets(presets) {
  if (presets.length == 0) {
    return [];
  }

  if (presets.length == 1) {
    return presets;
  }

  let bestPresets = [presets.at(0)];

  for (let i = 1; i < presets.length; i++) {
    if (presets.at(i).totalPrice() < bestPresets.at(0).totalPrice()) {
      bestPresets = [];
      bestPresets.push(presets.at(i));
    } else if (presets.at(i).totalPrice() == bestPresets.at(0).totalPrice()) {
      bestPresets.push(presets.at(i));
    }
  }

  return pickClosestWidthPresets(bestPresets);
}

/**
 *
 * @param {Array} presets The array containing the final presets
 * @returns {Array}
 */
function pickClosestWidthPresets(presets) {
  if (presets.length == 0) {
    return [];
  }

  if (presets.length == 1) {
    return presets;
  }

  let bestPresets = [presets.at(0)];

  for (let i = 1; i < presets.length; i++) {
    if (presets.at(i).availableWidth() < bestPresets.at(0).availableWidth()) {
      bestPresets = [];
      bestPresets.push(presets.at(i));
    } else if (presets.at(i).availableWidth() == bestPresets.at(0).availableWidth()) {
      bestPresets.push(presets.at(i));
    }
  }

  return bestPresets;
}
